import type { ScenarioSourceItem } from "@/lib/powtoon-be/types";

/** Safety margin before Gemini's 48-hour expiry. */
const EXPIRY_MARGIN_MS = 2 * 60 * 60 * 1000;

function isGeminiCacheValid(item: {
  gemini_file_uri?: string;
  gemini_expires_at?: string;
}): boolean {
  if (!item.gemini_file_uri || !item.gemini_expires_at) return false;
  const expiresAt = new Date(item.gemini_expires_at).getTime();
  return !Number.isNaN(expiresAt) && expiresAt > Date.now() + EXPIRY_MARGIN_MS;
}

/**
 * In-memory draft shape used by the editor. Carries the live `File` and
 * extracted text needed for generation; neither is persisted. On save these
 * are converted to `ScenarioSourceItem` and the file is uploaded as an
 * attachment so only metadata + object_id are stored.
 */
export type SourceInputDraft =
  | {
      kind: "upload";
      _draftId: string;
      file_name: string;
      mime?: string;
      size?: number;
      object_id?: string;
      _file?: File;
      /** Cached Gemini Files API URI; only valid until gemini_expires_at. */
      gemini_file_uri?: string;
      /** ISO timestamp when gemini_file_uri expires. */
      gemini_expires_at?: string;
      /** Transient upload state shown in the source list. */
      gemini_upload_state?: "idle" | "preparing" | "ready" | "error";
    }
  | { kind: "url"; _draftId: string; url: string }
  | { kind: "text"; _draftId: string; raw_text: string };

export function newDraftId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `d_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;
}

export function persistedToDraft(item: ScenarioSourceItem): SourceInputDraft {
  if (item.kind === "upload") {
    const cacheReady = isGeminiCacheValid(item);
    return {
      kind: "upload",
      _draftId: newDraftId(),
      object_id: item.object_id,
      file_name: item.file_name,
      mime: item.mime,
      size: item.size,
      gemini_file_uri: cacheReady ? item.gemini_file_uri : undefined,
      gemini_expires_at: cacheReady ? item.gemini_expires_at : undefined,
      gemini_upload_state: cacheReady ? "ready" : "idle",
    };
  }
  return { ...item, _draftId: newDraftId() };
}

export function draftToPersisted(d: SourceInputDraft): ScenarioSourceItem | null {
  if (d.kind === "upload") {
    if (!d.object_id) return null; // not yet uploaded — caller must upload first
    return {
      kind: "upload",
      object_id: d.object_id,
      file_name: d.file_name,
      mime: d.mime,
      size: d.size,
      gemini_file_uri: d.gemini_file_uri,
      gemini_expires_at: d.gemini_expires_at,
    };
  }
  if (d.kind === "url") return { kind: "url", url: d.url };
  return { kind: "text", raw_text: d.raw_text };
}
